import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ScrollReveal } from '@/components/landing/ScrollReveal';
import { ArrowLeft, ArrowRight, Clock, Tag } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABELS = { pillar: 'Foundation', industry: 'Industry', governance: 'Governance', insight: 'Insight' };

export default function ContentPage() {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/content/${slug}`).then(r => setContent(r.data)).catch(() => {}).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-aurocle-base flex items-center justify-center">
      <div className="text-aurocle-gold text-sm font-mono animate-pulse">Loading...</div>
    </div>
  );
  if (!content) return (
    <div className="min-h-screen bg-aurocle-base flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-heading text-2xl text-aurocle-text mb-4">Content not found</h1>
        <Link to="/insights" className="text-sm text-aurocle-gold">Return to Insights</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-aurocle-base text-aurocle-text font-body" data-testid="content-page">
      <div className="noise-overlay" aria-hidden="true" />

      <nav className="fixed top-0 left-0 right-0 z-50 bg-aurocle-base/95 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-base tracking-[0.15em] uppercase text-aurocle-text font-medium">
            Aurocle <span className="text-aurocle-gold">Agents</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/insights" className="text-xs uppercase tracking-wider text-aurocle-text-secondary hover:text-aurocle-gold transition-colors">Insights</Link>
            <Link to="/#contact" className="btn-gold text-xs py-2 px-5">Book Meeting</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-12 relative overflow-hidden">
        <div className="absolute top-20 left-1/3 w-[500px] h-[500px] bg-aurocle-gold/4 rounded-full blur-[180px] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <Link to="/insights" className="inline-flex items-center gap-2 text-xs text-aurocle-text-secondary hover:text-aurocle-gold transition-colors mb-6" data-testid="back-to-insights">
              <ArrowLeft size={12} /> All Insights
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="pill pill-gold text-[10px]">{TYPE_LABELS[content.type] || content.type}</span>
              <span className="text-[10px] font-mono text-aurocle-text-secondary">{content.category}</span>
              {content.reading_time && <span className="flex items-center gap-1 text-[10px] font-mono text-aurocle-text-secondary"><Clock size={10} /> {content.reading_time}</span>}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-aurocle-text mb-4 leading-tight">{content.title}</h1>
            {content.subtitle && <p className="text-lg text-aurocle-platinum-muted mb-6">{content.subtitle}</p>}
          </ScrollReveal>
        </div>
      </section>

      {/* Summary block (AI-friendly) */}
      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="glass-card p-8 border-aurocle-gold/10">
              <div className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60 mb-3">In Brief</div>
              <p className="text-base text-aurocle-text leading-relaxed" data-testid="content-hero-text">{content.hero_text}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Content sections */}
      <article className="pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-12">
          {(content.sections || []).map((section, i) => (
            <ScrollReveal key={i} delay={i * 60}>
              <div data-testid={`content-section-${i}`}>
                <h2 className="font-heading text-xl sm:text-2xl font-medium text-aurocle-text mb-4">{section.heading}</h2>
                <p className="text-base text-aurocle-platinum-muted leading-[1.8]">{section.body}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </article>

      {/* FAQ */}
      {content.faq && content.faq.length > 0 && (
        <section className="pb-12">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <ScrollReveal>
              <h2 className="font-heading text-xl font-medium text-aurocle-text mb-6">Frequently Asked</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {content.faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-white/6 bg-aurocle-surface px-6 data-[state=open]:border-aurocle-gold/15" data-testid={`content-faq-${i}`}>
                    <AccordionTrigger className="text-sm font-heading text-aurocle-text hover:text-aurocle-gold py-4 hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-aurocle-platinum-muted leading-relaxed pb-4">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Tags */}
      {content.tags && content.tags.length > 0 && (
        <section className="pb-8">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={12} className="text-aurocle-text-secondary" />
              {content.tags.map(t => (
                <span key={t} className="pill text-[10px]">{t}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <div className="glass-card p-10 text-center border-aurocle-gold/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-aurocle-gold/8 rounded-full blur-[80px] pointer-events-none" />
              <div className="relative">
                <p className="text-base text-aurocle-text mb-6">{content.cta_text || 'Explore how Aurocle Agents could be structured around your business.'}</p>
                <div className="flex items-center justify-center gap-4">
                  <Link to="/#contact" className="btn-gold" data-testid="content-cta-demo">Request Private Demo</Link>
                  <Link to="/#contact" className="btn-outline" data-testid="content-cta-meeting">Book Meeting</Link>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Related */}
      {content.related && content.related.length > 0 && (
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-6">Related Intelligence</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {content.related.map((r) => (
                <Link key={r.slug} to={`/insights/${r.slug}`} className="premium-card p-6 group" data-testid={`related-${r.slug}`}>
                  <div className="relative z-10">
                    <span className="pill text-[10px] mb-2 inline-block">{TYPE_LABELS[r.type] || r.type}</span>
                    <h4 className="font-heading text-sm font-medium text-aurocle-text group-hover:text-aurocle-gold transition-colors mb-1">{r.title}</h4>
                    <div className="flex items-center gap-1 text-xs text-aurocle-gold opacity-0 group-hover:opacity-100 transition-opacity mt-2">
                      Read <ArrowRight size={10} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <span className="text-xs text-aurocle-text-secondary">&copy; {new Date().getFullYear()} Aurocle Agents</span>
          <div className="flex gap-6">
            <Link to="/insights" className="text-xs text-aurocle-text-secondary hover:text-aurocle-gold transition-colors">Insights</Link>
            <Link to="/" className="text-xs text-aurocle-text-secondary hover:text-aurocle-gold transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
